import { z } from 'zod';

// ---------------------------------------------------------------------------
// Params
// ---------------------------------------------------------------------------

export const billingOrgIdParam = z.object({
  organizationId: z.string().uuid(),
});

export const subscriptionItemParams = z.object({
  organizationId: z.string().uuid(),
  itemId: z.string(),
});

// ---------------------------------------------------------------------------
// Bodies
// ---------------------------------------------------------------------------

export const spendCapBody = z.object({
  enabled: z.boolean(),
});

export const growthPacksBody = z.object({
  quantity: z.number().int().min(1).max(100).default(1),
});

export const portalSessionBody = z.object({
  return_url: z.string().url().optional(),
});

export const checkoutSessionBody = z.object({
  tier: z.enum(['solo', 'team']),
});

export const updateBillingCurrencyBody = z.object({
  currency: z.enum(['USD', 'BRL']),
});

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export const invoicesQuery = z.object({
  limit: z.string().regex(/^\d+$/).optional(),
});

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

export interface BillingOrganizationItem {
  organization_id: string;
  organization_name: string;
  billing_currency: string;
  created_at: string;
  organization_admin_user: {
    is_owner: boolean;
    enabled: boolean;
  }[];
}

export interface BillingOrganizationsResponse {
  success: true;
  data: BillingOrganizationItem[];
}

/**
 * The two closed billing unions, declared once. The Rekor entitlement projection
 * (below) and `BillingRecord` both spell them, and a third hand-written copy is how
 * `cancelled`/`canceled` drift starts.
 */
export const planKeySchema = z.enum(['free', 'solo', 'team']);
export const billingStatusSchema = z.enum(['active', 'past_due', 'cancelled']);
export type PlanKey = z.infer<typeof planKeySchema>;
export type BillingStatusKey = z.infer<typeof billingStatusSchema>;

/**
 * How the org's entitlement is PAID FOR — deliberately not the same question as `plan_key`,
 * which says what it is entitled TO.
 *
 * `comped` exists because the two were conflated: a platform-console override wrote
 * `plan_type: 'paid'` on an org with no Stripe subscription, producing a row byte-identical
 * to a corrupted paid one — no `current_period_start/end`, so no entitlement window, so no
 * Rekor projection and a 503 on every Data/Bases provisioning operation (production, org
 * 0de5878a, 2026-08-18). A comped org is entitled exactly like a paid one; what it lacks is
 * an invoice, and that is what this value names.
 */
export const billingPlanTypeSchema = z.enum(['free', 'paid', 'comped']);
export type BillingPlanType = z.infer<typeof billingPlanTypeSchema>;

/**
 * Does this plan type carry the SELF-RENEWING rolling window (`free_plan_started_at` plus a
 * fixed period, advanced by OrgDO's lazy rollover) rather than a Stripe billing period?
 *
 * The one place that question is answered, because three call sites must agree or the org
 * breaks in a different way at each: the rollover (a window that never advances), the
 * operations-quota read (a `resets_at` of null), and the Rekor projection's window source
 * (an unbuildable projection). Comped rows have no Stripe period to read, so they take the
 * same rolling window the free tier already owns — no new machinery, and renewal arrives via
 * the same lazy rollover.
 */
export function isRollingWindowPlanType(planType: string | null | undefined): boolean {
  return planType === 'free' || planType === 'comped';
}

/**
 * Is this row actually being billed by Stripe RIGHT NOW — `plan_type: 'paid'` with a
 * billing period that has not already elapsed?
 *
 * `plan_type` alone cannot answer it, and that gap is what the comped incident was made
 * of. `cancelBilling` leaves `current_period_end` at the ended subscription's date (only
 * the plan and the status change), so a console override that wrote `paid` back over such
 * a row produces a period that is real but PAST. Its projection then carries a
 * `window_resets_at` already behind the clock — permanently stale, with Rekor's import
 * gate refusing indefinitely and neither the sweep (the rebuild is byte-identical, so the
 * idempotency skip drops it) nor the renewal webhook (there is no subscription to renew)
 * able to converge.
 *
 * So the question this answers is "is Stripe going to move this window?" — the test behind
 * the admin plan PATCH's refusal, where a human is present to read the message. Deciding
 * whether to make an UNATTENDED write against a row is a different, more conservative
 * question; `isStrandedPaidRow` below is that one.
 */
export function isStripeBilledPlan(
  planType: string | null | undefined,
  currentPeriodEnd: string | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (planType !== 'paid' || !currentPeriodEnd) return false;
  const endMs = Date.parse(currentPeriodEnd);
  return Number.isFinite(endMs) && endMs > nowMs;
}

/**
 * Does this row say `paid` while PROVABLY having no subscription behind it?
 *
 * The conservative twin of `isStripeBilledPlan`, and the two are deliberately not each
 * other's negation. That one asks "is Stripe going to move this window?" and is what a
 * human-facing refusal uses, because a human is present to read the message. This one asks
 * "can we be certain nobody is paying?" and is what unattended writes use — the fleet
 * repair, and the console's decision to OFFER a comp control at all.
 *
 * An elapsed period alone is NOT proof. Stripe dunning leaves a real subscriber sitting at
 * `plan_type: 'paid'` with `subscription_status: 'past_due'` and an elapsed period for days
 * or weeks (`handlePaymentFailed` writes only the status), and a renewal webhook can simply
 * be late. Comping such an org would erase the arrears marker, restamp its window — Rekor's
 * counter-reset trigger — and take a paying customer off the Stripe meter. Only two shapes
 * are proof: no period was ever written, or the subscription is cancelled.
 */
export function isStrandedPaidRow(
  planType: string | null | undefined,
  currentPeriodEnd: string | null | undefined,
  subscriptionStatus: string | null | undefined,
): boolean {
  if (planType !== 'paid') return false;
  if (!currentPeriodEnd) return true;
  const status = String(subscriptionStatus ?? 'active').toLowerCase();
  return status === 'cancelled' || status === 'canceled' || status === 'incomplete_expired';
}

export interface BillingRecord {
  organization_id: string;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  plan_type: BillingPlanType;
  plan_key: PlanKey;
  plan_kind: 'free' | 'base' | 'addon';
  billing_cycle: 'monthly' | 'annual';
  display_name?: string;
  period_start: string | null;
  period_end: string | null;
  spend_cap_enabled: boolean;
  billing_status: BillingStatusKey;
  created_at: string;
  updated_at: string;
}

export interface BillingMetric {
  billing_metric_id: string;
  organization_id: string;
  metric_type: 'messages' | 'users' | 'storage' | 'api_calls';
  monthly_included: number;
  consumed_subscription: number;
  alert_80_sent: boolean;
  alert_95_sent: boolean;
  alert_100_sent: boolean;
  last_consumed_at: string | null;
  last_reset_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface BillingMetricsHistory {
  history_id: string;
  organization_id: string;
  period_start: string;
  period_end: string;
  metric_type: 'messages' | 'users' | 'storage' | 'api_calls';
  monthly_included: number;
  consumed_subscription: number;
  created_at: string;
}

export interface BillingData {
  billing: BillingRecord;
  metrics: BillingMetric[] | null;
  history: BillingMetricsHistory[] | null;
  growth_pack: {
    operations_per_unit: number;
  } | null;
  is_owner: boolean;
  billing_currency: string;
}

export interface GetBillingDataResponse {
  success: true;
  data: BillingData;
}

export interface UpdateSpendCapResponse {
  success: true;
  data: { spend_cap_enabled: boolean };
}

export interface AddGrowthPackResponse {
  success: true;
  data: {
    subscription_item_id: string;
    quantity: number;
    messages_added: number;
    seats_added: number;
  };
}

export interface UpdateBillingCurrencyResponse {
  success: true;
  data: { currency: 'USD' | 'BRL' };
}

export interface RemoveSubscriptionItemResponse {
  success: true;
  data: { removed: string };
}

export interface PortalSessionResponse {
  success: true;
  data: { url: string };
}

export interface Invoice {
  id: string;
  amount: number;
  currency: string;
  status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void' | null;
  created: number;
  period_start: number;
  period_end: number;
  invoice_pdf: string | null | undefined;
  hosted_invoice_url: string | null | undefined;
}

export interface GetInvoicesResponse {
  success: true;
  data: Invoice[];
}

export interface CheckoutSessionResponse {
  success: true;
  data: { url: string | null; mode: 'portal' | 'checkout'; session_id?: string };
}

// ---------------------------------------------------------------------------
// Effective-plan resolution
//
// One definition, shared: the entitlement projection decides what Rekor ENFORCES with
// it, and the data-usage read decides what the customer is SHOWN. Those two answers
// have to be the same answer, so they come from the same pair of functions rather than
// from a re-derivation on the read side.
// ---------------------------------------------------------------------------

/**
 * The three billing-status literals, from whatever spelling `billing.subscription_status`
 * holds — the column is written by several callers and validated by none. Stripe spells
 * the terminal state `canceled`; WayAI's contract spells it `cancelled`, and
 * keeping WayAI's spelling is what makes a `billing_status` grep find both sides.
 *
 * A COMPED org is `active` by construction, whatever the column says. `billing_status`
 * reports the state of an invoice and a comp has none, so `past_due` and `cancelled` are
 * both uninterpretable for it — and the value is not inert here despite being
 * informational: `effectivePlanKey` reads `cancelled` as a downgrade, so a status left
 * over from the org's pre-comp Stripe life would hand a comped Team org free allowances.
 * The admin write normalizes the column too; this is the half that holds for a row written
 * before that gate existed.
 */
export function normalizeBillingStatus(planType: string, raw: unknown): BillingStatusKey {
  if (planType === 'comped') return 'active';
  const value = String(raw ?? 'active').toLowerCase();
  if (value === 'past_due' || value === 'unpaid') return 'past_due';
  if (value === 'cancelled' || value === 'canceled' || value === 'incomplete_expired') {
    return 'cancelled';
  }
  return 'active';
}

/**
 * The EFFECTIVE plan, not the subscribed one: a cancelled Solo org resolves to
 * `free` with free allowances beside `billing_status: 'cancelled'`.
 *
 * A comped org resolves like a paid one — its `plan_key` IS its entitlement, which is what
 * an operator grants when they comp it. Nothing on the wire distinguishes the two, and
 * that is deliberate: Rekor enforces an entitlement, and how it is paid for is
 * WayAI's concern alone.
 */
export function effectivePlanKey(
  planType: string,
  planKey: unknown,
  status: BillingStatusKey,
): PlanKey {
  if (status === 'cancelled' || planType === 'free') return 'free';
  // Parsed against the schema rather than a hand-written `=== 'solo' || === 'team'`:
  // that spelling is a second copy of `planKeySchema`'s membership, and a tier added to
  // the enum would type-check everywhere while silently resolving to `free` here — on
  // the gauge AND in the entitlement the projection makes Rekor enforce.
  const parsed = planKeySchema.safeParse(planKey);
  return parsed.success ? parsed.data : 'free';
}

/**
 * The whole effective-plan prelude, as ONE step.
 *
 * The plan type is read once and shared by the status normalization and the key
 * resolution — read twice, those two can disagree. Exported as a composition rather than
 * as its two halves because both call sites need the same three-line sequence, and a
 * sequence spelled twice is where the `?? 'free'` default drifts.
 */
export function resolveEffectivePlan(row: {
  plan_type?: unknown;
  plan_key?: unknown;
  subscription_status?: unknown;
}): { planType: string; billingStatus: BillingStatusKey; planKey: PlanKey } {
  const planType = String(row.plan_type ?? 'free');
  const billingStatus = normalizeBillingStatus(planType, row.subscription_status);
  return { planType, billingStatus, planKey: effectivePlanKey(planType, row.plan_key, billingStatus) };
}

/**
 * A `pricing_plan` allowance column, or null when the row does not carry a usable number.
 *
 * ONE definition, shared by the projection builder and the billing-surface read for the
 * same reason `resolveEffectivePlan` is: an un-migrated row (or a cached snapshot from
 * before the `rekor_*` columns landed) yields `undefined` here, and both callers refuse
 * rather than coerce it to 0 — a zero allowance is a schema-valid entitlement granting
 * nothing, and a permanently-full gauge. Two copies of that rule is how the number shown
 * and the number enforced come to disagree.
 */
export function planAllowanceValue(raw: unknown): number | null {
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : null;
}

// ---------------------------------------------------------------------------
// Data usage surfacing — the READ half
//
// Two meters, one customer vocabulary:
// *conversation operations* and *data operations*. The conversation meter is
// already carried by `BillingMetric`; this block adds the data one.
// ---------------------------------------------------------------------------

/**
 * The data meter counts in TENTHS (a point read costs 1, a write or a list/SQL row costs
 * 10). The customer-facing unit is a whole **data operation**, and the invoice line, the
 * allowance table and the overage rate are all quoted in it — so the tenths→ops division
 * is a pricing rule, defined once here rather than re-derived per surface.
 */
export const DATA_OPS_TENTHS_PER_OP = 10;

/**
 * Tenths → whole data operations, for DISPLAY.
 *
 * Rounds to nearest. Every allowance is an exact multiple of ten, so only a usage
 * numerator can round at all; nearest is the unbiased choice between under-reporting
 * consumption (a surprise at the cap) and over-reporting it (a phantom charge). It is
 * deliberately NOT the overage quantity formula, which is a `ceil` over the *difference*
 * and belongs to the metering PR, not to a gauge.
 *
 * Percentages must be computed from the raw tenths, never from this — dividing twice
 * makes a full gauge read 99 %.
 */
export function dataOpsFromTenths(tenths: number): number {
  return Math.round(tenths / DATA_OPS_TENTHS_PER_OP);
}

/**
 * The `usage` object of the data service's `GET /v1/billing/usage`.
 *
 * **Only `usage` is authoritative on that response**: `plan`, `status`, `limits`
 * and `synced_at` are WayAI's own projection round-tripping back, so this schema covers
 * the counters ALONE and the proxy route never reads the other four. The legacy
 * `*_this_month` / `month_key` spellings are the far side's published field names — kept
 * on the wire we parse, translated to the capability vocabulary on the way out.
 *
 * Deliberately NOT `.strict()`, and every counter is `.catch(0)`-free by intent: the
 * producer is a separate Worker on its own deploy cadence, so an ADDED field must not
 * fail the parse, while a missing or malformed counter must — a silent `0` on a billing
 * gauge reads as "you have used nothing", which is the one wrong answer.
 */
export const dataUsageCountersSchema = z.object({
  ops_tenths_this_month: z.number().nonnegative(),
  records_count: z.number().nonnegative(),
  month_key: z.string(),
  // The closed window's meter reading (the prior-window snapshot). OPTIONAL for the same
  // reason this object is not `.strict()`: the producer deploys on its own cadence, and a
  // required field would fail the parse for every counter on any producer build that predates
  // it — turning an additive wire change into an outage of the gauge above. Absence and a
  // non-matching key are the SAME outcome for a reader (do not bill), so
  // nothing is lost by tolerating it.
  prior_window_key: z.string().optional(),
  prior_window_ops_tenths: z.number().nonnegative().optional(),
});

export type DataUsageCounters = z.infer<typeof dataUsageCountersSchema>;

// ---------------------------------------------------------------------------
// Data-meter overage — the arming half. These helpers are the single CODE home
// for each rule.
// ---------------------------------------------------------------------------

/**
 * The enforcement ceiling, as a multiplier over the included total.
 *
 * A constant rather than a `platform_config` value on purpose: it is not an operator dial but
 * a term of the wire contract — Rekor reads `ops_tenths_included` as "refuse past this", so
 * changing it changes what a metered org may consume and what its capped invoice can reach.
 * This is the one place that number is spelled.
 */
export const DATA_OPS_OVERAGE_CEILING_MULTIPLIER = 2;

/** The Stripe meter event name and price lookup-key prefix. */
export const DATA_OPS_METER_EVENT_NAME = 'wayai_data_ops';
export const DATA_OPS_LOOKUP_KEY_PREFIX = 'wayai_data_ops';

/**
 * Is this org's data meter BILLED for overage — the predicate, in one place?
 *
 * Both halves are load-bearing and neither implies the other. `isStripeBilledPlan` excludes
 * free and comped (no invoice exists to add a line to, and a comp has no headroom to
 * accrue one silently); the `active` test excludes `past_due`, so a delinquent org cannot
 * deepen arrears it is already failing to pay, and `cancelled`, whose effective plan is free.
 */
export function isDataOpsMeteredOrg(
  planType: string | null | undefined,
  currentPeriodEnd: string | null | undefined,
  /** The NORMALIZED status (`resolveEffectivePlan().billingStatus`), never the raw column. */
  billingStatus: BillingStatusKey,
  nowMs: number = Date.now(),
): boolean {
  return isStripeBilledPlan(planType, currentPeriodEnd, nowMs) && billingStatus === 'active';
}

/**
 * What the projection PUTS on the wire for `ops_tenths_included`.
 *
 * For a metered org that is the enforcement ceiling, not the included total — the field's
 * meaning to Rekor ("refuse past this") is unchanged, which is why overage needs no
 * `contract_version` bump and no far-side change at all. Every other org projects the
 * included total unchanged, so free stays a hard refusal at its allowance.
 *
 * The included total is NOT recoverable from the projected value by dividing: a reader that
 * needs it (the period-end quantity below, the in-app gauge) resolves it from the plan rows,
 * which is why `resolveDataAllowanceTotals` exists separately.
 */
export function projectedOpsTenthsIncluded(includedTenths: number, metered: boolean): number {
  return metered ? includedTenths * DATA_OPS_OVERAGE_CEILING_MULTIPLIER : includedTenths;
}

/**
 * The period's billable quantity: whole ops past the INCLUDED total, rounded up,
 * computed once per org per period.
 *
 * `ceil` over the difference, deliberately not `dataOpsFromTenths`'s round-to-nearest — that
 * one is for a gauge, this one decides a charge, and the two must not be confused (the
 * display helper's own doc says so). Clamped at zero so an under-consuming org yields 0, which
 * is SKIPPED rather than sent as a zero meter record.
 *
 * Bounded before Stripe sees it by the ceiling above: a metered org cannot consume past
 * 2 × included, so the line is capped at the included total over again.
 */
export function dataOpsOverageQuantity(usedTenths: number, includedTenths: number): number {
  const overage = usedTenths - includedTenths;
  if (!Number.isFinite(overage) || overage <= 0) return 0;
  return Math.ceil(overage / DATA_OPS_TENTHS_PER_OP);
}

/**
 * How many Growth Packs this org's allowances may count — the EFFECTIVE quantity, not the
 * stored column.
 *
 * A pack is an item on a live Stripe subscription, so an org whose effective plan is `free`
 * holds none whatever the column says. The column is written by exactly one path (the
 * subscription webhook), and cancellation is not that path: `OrgDO.cancelBilling` writes the
 * downgrade in raw SQL and leaves the quantity behind. Read unconditionally, that stale value
 * grants a cancelled org its packs forever — 550 000 tenths and 610 000 records on a two-pack
 * downgrade, against free's 50 000 / 10 000 — which is exactly the coupling `effectivePlanKey`
 * exists to break ("a cancelled Solo org resolves to `free` with free allowances").
 *
 * Gated on the effective key rather than on `plan_type` so it holds for every writer that
 * forgets, present and future — a guard at the point of USE cannot be bypassed by a new
 * downgrade path the way a fix to one writer can.
 */
export function effectivePackQuantity(planKey: PlanKey, storedQuantity: unknown): number {
  if (planKey === 'free') return 0;
  const quantity = Number(storedQuantity);
  return Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 0;
}

/** The three additive data allowances, resolved to one org's totals. */
export interface DataAllowanceTotals {
  ops_tenths_included: number;
  max_records: number;
  import_rows_included: number;
}

/** The `pricing_plan` columns these totals are built from. */
export interface DataAllowanceSource {
  rekor_ops_tenths_included?: unknown;
  rekor_max_records?: unknown;
  rekor_import_rows_included?: unknown;
}

/**
 * Base plan + `quantity` × Growth Pack, on all three axes ("Growth Pack is the tier
 * step" — each pack is the Solo→Team delta, so Team = Solo + one pack and the ladder extends
 * linearly).
 *
 * `null` — never a partial or a zero-coerced total — when any base allowance is unreadable, for
 * the reason `buildProjectionContent` refuses rather than coerces: a schema-VALID projection
 * granting nothing gets fingerprinted and recorded, and the idempotency skip then suppresses
 * the correction on every later sweep.
 *
 * A missing PACK row with a nonzero quantity is the same refusal, and that asymmetry is
 * deliberate: quantity 0 needs no pack row (the overwhelmingly common case, and the only one
 * before any pack is sold), while quantity > 0 with no row would silently bill a customer for
 * capacity their entitlement never carries — the exact failure the pricing-page gate was
 * holding the line against until this shipped.
 */
export function resolveDataAllowanceTotals(
  basePlan: DataAllowanceSource | null | undefined,
  packPlan: DataAllowanceSource | null | undefined,
  packQuantity: number,
): DataAllowanceTotals | null {
  if (!basePlan) return null;
  const baseOps = planAllowanceValue(basePlan.rekor_ops_tenths_included);
  const baseRecords = planAllowanceValue(basePlan.rekor_max_records);
  const baseImports = planAllowanceValue(basePlan.rekor_import_rows_included);
  if (baseOps === null || baseRecords === null || baseImports === null) return null;

  const quantity = Number(packQuantity);
  if (!Number.isFinite(quantity) || quantity <= 0) {
    return {
      ops_tenths_included: baseOps,
      max_records: baseRecords,
      import_rows_included: baseImports,
    };
  }
  const packs = Math.floor(quantity);

  if (!packPlan) return null;
  const packOps = planAllowanceValue(packPlan.rekor_ops_tenths_included);
  const packRecords = planAllowanceValue(packPlan.rekor_max_records);
  const packImports = planAllowanceValue(packPlan.rekor_import_rows_included);
  if (packOps === null || packRecords === null || packImports === null) return null;

  return {
    ops_tenths_included: baseOps + packs * packOps,
    max_records: baseRecords + packs * packRecords,
    import_rows_included: baseImports + packs * packImports,
  };
}

/**
 * What the org is entitled to on the data meter.
 *
 * Sourced from WayAI's OWN `pricing_plan` row for the org's effective plan key — the same
 * resolution the entitlement projection makes — never from the `limits` object the data
 * service echoes back.
 */
export interface DataUsageAllowance {
  /** Tenths, matching the meter's granularity. Divide with `dataOpsFromTenths` to display. */
  ops_tenths_included: number;
  max_records: number;
}

export interface DataUsageCounts {
  /** Tenths consumed in the current window. */
  ops_tenths_used: number;
  records_used: number;
  /** The window these counters belong to, as the data service labels it. */
  window_key: string;
}

/**
 * What this org's data meter can say. THREE outcomes, not "counters or nothing" — the
 * absent case splits, and collapsing it is how the dominant population gets the wrong
 * answer:
 *
 * - `measured` — real counters, including genuinely zero ones. An org that holds an
 *   entitlement and has spent nothing lands HERE: the data service resolves its
 *   projection, its usage record comes back empty, and the zeros are true.
 * - `unused` — WayAI has no record of this org ever reaching the data product
 *   (`rekor_bearing_at` is null), so no base was created and no data token was minted.
 *   Its counters are zero by construction and the data service is never asked. This is
 *   the ordinary state for most orgs, not a fault.
 * - `unresolved` — the data service holds no usable entitlement projection for an org
 *   that HAS reached it (a pre-cutover blob, a `contract_version` mismatch, a lost KV
 *   value, a nudge that never landed). Its resolver still reads the real counters there
 *   — "a request that cannot be ENFORCED is still one that must be BILLED" — and the
 *   billing route drops them, so what reaches us is an absence of knowledge. This is the
 *   one state where the org's real consumption is nonzero and unknown to us, and where
 *   its base creation and token mints are being refused by the fail-closed gates.
 *
 * `unused` and `unresolved` both carry no counters and mean opposite things: one is "you
 * have not started", the other is "we cannot see your usage while you are being refused".
 * Rendering zeros for the second, or a failure for the first, are both wrong answers.
 */
export type DataUsageReading =
  | { state: 'measured'; counters: DataUsageCounts }
  | { state: 'unused' }
  | { state: 'unresolved' };

export interface GetDataUsageResponse {
  success: true;
  data: {
    /** The EFFECTIVE plan key the allowance was resolved for. */
    plan_key: PlanKey;
    /** Always reported — the org's plan entitles it whether or not it has used any. */
    allowance: DataUsageAllowance;
    reading: DataUsageReading;
  };
}

// ---------------------------------------------------------------------------
// Rekor entitlement projection
//
// WayAI is the sole writer of Rekor's `entitlement:{org_id}` KV key; Rekor
// owns `usage:{org_id}` and never authors this shape. The schema lives here rather
// than beside the writer so `contract_version` refusal on the Rekor side is a schema
// parse instead of hand-rolled field checks in two repos.
// ---------------------------------------------------------------------------

/** Shape version. Rekor refuses to interpret a higher one. */
export const REKOR_ENTITLEMENT_CONTRACT_VERSION = 1;

/** KV key prefix, unchanged from Rekor's existing `entitlement:` literal. */
export const REKOR_ENTITLEMENT_KEY_PREFIX = 'entitlement:';

export const entitlementProjectionSchema = z
  .object({
    contract_version: z.literal(REKOR_ENTITLEMENT_CONTRACT_VERSION),
    /**
     * Monotonic per org, WayAI-assigned. An ordering guard for concurrent WayAI
     * writers (a plan change racing a token mint) — NOT a cross-repo CAS. Gaps are
     * expected: a version is allocated per push attempt, and an idempotency skip
     * burns none while a failed KV put burns one.
     */
    projection_version: z.number().int().nonnegative(),
    /** Diagnostic only — never gates. Freshness is `window_resets_at`'s job. */
    written_at: z.string().datetime(),

    /** The WayAI org UUID. The `account_id` spelling is Rekor's documented carve-out. */
    account_id: z.string().uuid(),
    /** The EFFECTIVE plan, not the subscribed one. Never gates. */
    plan_key: planKeySchema,
    /** Informational only. `cancelled` spelling matches `BillingRecord`. */
    billing_status: billingStatusSchema,

    /** Tenths, matching Rekor's meter granularity (write 10, point-read 1, list/SQL 10). */
    ops_tenths_included: z.number().int().nonnegative(),
    max_records: z.number().int().nonnegative(),
    /** Its own field, no longer derived from `max_records`. */
    import_rows_included: z.number().int().nonnegative(),
    /** REQUIRED — absence is malformed, never coerced to `false`. */
    production_bases: z.boolean(),

    /** Opaque to Rekor; compared for equality only, to reset `usage:` counters. */
    window_key: z.string().min(1),
    /**
     * The window's TRUE lower bound, for the reconcile's `operations_log` sum.
     *
     * OPTIONAL, and that is not a rollout convenience — it is what "never gates" means
     * on the writer's side. The tier sources can be absent (a paid row carrying
     * `current_period_end` with no `current_period_start`) and inverted pairs are a
     * diagnostic, so requiring it here would let an inert field throw the strict
     * parse and leave the org with NO projection — permanently stale, with the
     * fail-closed gates refusing base creation and 503-ing every import. The
     * writer emits it whenever it has a sound value and omits it otherwise; Rekor's
     * observed-flip fallback covers the gap, and rollout step 3 keeps that fallback
     * for exactly this reason.
     */
    window_start: z.string().datetime().optional(),
    /** The sole staleness authority. Never triggers a counter reset. */
    window_resets_at: z.string().datetime(),
  })
  .strict();

export type EntitlementProjection = z.infer<typeof entitlementProjectionSchema>;

/**
 * `rekor-entitlement-refresh` queue body — Rekor produces, WayAI consumes.
 *
 * Identity only. The consumer self-loads OrgDO state, lets the lazy free-plan
 * rollover run, rebuilds the projection and pushes it, so it never reads what Rekor
 * observed. `reason` carries the consumer's log line, tells "the window rolled" apart from
 * "the projection pipeline is broken" in an incident, and SELECTS the push trigger — see
 * `TRIGGER_BY_REASON` in the consumer. Adding a value here is therefore a behavioral change
 * on the recovery path, not a new log string.
 *
 * Deliberately NOT `.strict()`: the producer is a separate repo on its own deploy
 * cadence, and an added field must not dead-letter every nudge — the recovery path
 * that is the ONLY thing that clears staleness.
 */
export const rekorEntitlementRefreshMessageSchema = z.object({
  org_id: z.string().uuid(),
  reason: z.enum(['window_elapsed', 'projection_missing', 'projection_malformed']),
});

export type RekorEntitlementRefreshMessage = z.infer<typeof rekorEntitlementRefreshMessageSchema>;
